import type { Capability } from '@domain';

import type { DocumentKind } from '@domain';

export type FieldType =
  | 'text'
  | 'date'
  | 'num'
  | 'area'
  | 'toggle'
  | 'contact'
  | 'currency'
  | 'account'
  | 'tax'
  | 'amounts'
  /*
    Which training course this bill belongs to.

    Not a Xero field — it is our own link, written after Xero accepts the
    bill. It sits on the form rather than only on the course screen because
    "go back afterwards and attach it" is the step that gets skipped, and a
    course's costs are only as good as the bills somebody remembered to
    link. Drawn only for a company that actually runs courses.
  */
  | 'training';

/**
 * Xero's account types, exactly as the Accounting API enumerates them.
 *
 * Here because some fields take only certain kinds of account, and offering
 * the rest is offering a refusal.
 */
export type AccountType =
  | 'BANK'
  | 'CURRENT'
  | 'CURRLIAB'
  | 'DEPRECIATN'
  | 'DIRECTCOSTS'
  | 'EQUITY'
  | 'EXPENSE'
  | 'FIXED'
  | 'INVENTORY'
  | 'LIABILITY'
  | 'NONCURRENT'
  | 'OTHERINCOME'
  | 'OVERHEADS'
  | 'PREPAYMENT'
  | 'REVENUE'
  | 'SALES'
  | 'TERMLIAB'
  | 'PAYG';

export interface FieldDef {
  k: string;
  label: string;
  /** The Xero field this maps to, shown under the label. */
  api: string;
  type: FieldType;
  /** Width in twelfths. */
  span: number;
  req?: boolean;
  ph?: string;
  /**
   * The account types this field will actually accept.
   *
   * Only set where Xero restricts it and we can say so on the evidence —
   * the OpenAPI description, or a rule Xero's own screens enforce. Absent
   * means every account, which is the honest answer where we do not know:
   * over-filtering hides an account somebody needed and leaves them stuck,
   * which is worse than showing one Xero will refuse.
   */
  accounts?: readonly AccountType[];
  /**
   * Draw this only when another field on the form is switched on.
   *
   * For fields that exist in Xero only under a condition. Drawing one
   * unconditionally asks for something that will be thrown away.
   */
  onlyWhen?: string;
}

export interface SectionDef {
  title: string;
  fields: FieldDef[];
}

export interface FormDef {
  title: string;
  root: string;
  api: string;
  endpoint: string;
  lines: boolean;
  discount: boolean;
  sections: SectionDef[];
  /** The list this form returns to. */
  backTo: string;
}

export const FORMS: Record<DocumentKind, FormDef> = {
  invoice: {
    title: 'New invoice',
    root: 'Sales',
    api: 'Invoice · Type = ACCREC',
    endpoint: 'PUT /Invoices',
    lines: true,
    discount: true,
    backTo: 'list:invoices',
    sections: [
      {
        title: 'Invoice details',
        fields: [
          { k: 'contactId', label: 'Customer', api: 'Contact.ContactID', type: 'contact', span: 4, req: true },
          { k: 'date', label: 'Issue date', api: 'Date', type: 'date', span: 2, req: true },
          { k: 'dueDate', label: 'Due date', api: 'DueDate', type: 'date', span: 2 },
          { k: 'invoiceNumber', label: 'Invoice number', api: 'InvoiceNumber', type: 'text', span: 2, ph: 'auto' },
          { k: 'reference', label: 'Reference', api: 'Reference', type: 'text', span: 2 },
          { k: 'currencyCode', label: 'Currency', api: 'CurrencyCode', type: 'currency', span: 2 },
          { k: 'amountsAre', label: 'Amounts are', api: 'LineAmountTypes', type: 'amounts', span: 3 },
        ],
      },
    ],
  },
  bill: {
    title: 'New bill',
    root: 'Purchases',
    api: 'Invoice · Type = ACCPAY',
    endpoint: 'PUT /Invoices',
    lines: true,
    discount: false,
    backTo: 'list:bills',
    sections: [
      {
        title: 'Bill details',
        fields: [
          { k: 'contactId', label: 'Supplier', api: 'Contact.ContactID', type: 'contact', span: 4, req: true },
          { k: 'date', label: 'Date', api: 'Date', type: 'date', span: 2, req: true },
          { k: 'dueDate', label: 'Due Date', api: 'DueDate', type: 'date', span: 2 },
          { k: 'reference', label: 'Reference', api: 'Reference', type: 'text', span: 4, ph: "Supplier's own invoice number" },
          { k: 'currencyCode', label: 'Currency', api: 'CurrencyCode', type: 'currency', span: 2 },
          { k: 'amountsAre', label: 'Amounts are', api: 'LineAmountTypes', type: 'amounts', span: 3 },
          { k: 'trainingDeliveryId', label: 'For a training course', api: 'Not sent to Xero', type: 'training', span: 4 },
        ],
      },
    ],
  },
  quote: {
    title: 'New quote',
    root: 'Sales',
    api: 'Quote',
    endpoint: 'PUT /Quotes',
    lines: true,
    discount: true,
    backTo: 'list:quotes',
    sections: [
      {
        title: 'Quote details',
        fields: [
          { k: 'contactId', label: 'Customer', api: 'Contact.ContactID', type: 'contact', span: 4, req: true },
          { k: 'date', label: 'Issue date', api: 'Date', type: 'date', span: 2, req: true },
          { k: 'expiryDate', label: 'Expiration date', api: 'ExpiryDate', type: 'date', span: 2 },
          { k: 'quoteNumber', label: 'Quote number', api: 'QuoteNumber', type: 'text', span: 2, ph: 'QU-0001' },
          { k: 'reference', label: 'Reference', api: 'Reference', type: 'text', span: 2 },
          { k: 'currencyCode', label: 'Currency', api: 'CurrencyCode', type: 'currency', span: 2 },
          { k: 'amountsAre', label: 'Tax', api: 'LineAmountTypes', type: 'amounts', span: 3 },
        ],
      },
      {
        title: 'Title, summary and terms',
        fields: [
          { k: 'titleText', label: 'Title', api: 'Title', type: 'text', span: 6 },
          { k: 'summary', label: 'Summary', api: 'Summary', type: 'text', span: 6 },
          { k: 'terms', label: 'Terms', api: 'Terms', type: 'area', span: 12 },
        ],
      },
    ],
  },
  po: {
    title: 'New purchase order',
    root: 'Purchases',
    api: 'PurchaseOrder',
    endpoint: 'PUT /PurchaseOrders',
    lines: true,
    discount: false,
    backTo: 'list:pos',
    sections: [
      {
        title: 'Order details',
        fields: [
          { k: 'contactId', label: 'Supplier', api: 'Contact.ContactID', type: 'contact', span: 4, req: true },
          { k: 'date', label: 'Date raised', api: 'Date', type: 'date', span: 2, req: true },
          { k: 'deliveryDate', label: 'Delivery date', api: 'DeliveryDate', type: 'date', span: 2 },
          { k: 'orderNumber', label: 'Order number', api: 'PurchaseOrderNumber', type: 'text', span: 2, ph: 'auto' },
          { k: 'reference', label: 'Reference', api: 'Reference', type: 'text', span: 2 },
          { k: 'currencyCode', label: 'Currency', api: 'CurrencyCode', type: 'currency', span: 2 },
          { k: 'amountsAre', label: 'Amounts are', api: 'LineAmountTypes', type: 'amounts', span: 3 },
        ],
      },
      {
        title: 'Delivery',
        fields: [
          { k: 'deliveryAddress', label: 'Delivery address', api: 'DeliveryAddress', type: 'area', span: 6 },
          { k: 'attentionTo', label: 'Attention', api: 'AttentionTo', type: 'text', span: 3 },
          { k: 'telephone', label: 'Telephone', api: 'Telephone', type: 'text', span: 3 },
          { k: 'deliveryInstructions', label: 'Delivery instructions', api: 'DeliveryInstructions', type: 'area', span: 12, ph: 'Up to 500 characters' },
        ],
      },
    ],
  },
  contact: {
    title: 'New contact',
    root: 'Contacts',
    api: 'Contact',
    endpoint: 'PUT /Contacts',
    lines: false,
    discount: false,
    backTo: 'list:customers',
    sections: [
      {
        title: 'Contact details',
        fields: [
          { k: 'name', label: 'Contact name', api: 'Name', type: 'text', span: 6, req: true, ph: 'Must be unique in the organisation' },
          { k: 'accountNumber', label: 'Account number', api: 'AccountNumber', type: 'text', span: 3 },
          { k: 'firstName', label: 'First name', api: 'FirstName', type: 'text', span: 3 },
          { k: 'lastName', label: 'Last name', api: 'LastName', type: 'text', span: 3 },
          { k: 'email', label: 'Email', api: 'EmailAddress', type: 'text', span: 5 },
          { k: 'phone', label: 'Phone', api: 'Phones[] (DEFAULT)', type: 'text', span: 4 },
        ],
      },
      {
        title: 'Addresses',
        fields: [
          { k: 'street', label: 'Street address', api: 'Addresses[] (STREET)', type: 'area', span: 6 },
          { k: 'pobox', label: 'Postal address', api: 'Addresses[] (POBOX)', type: 'area', span: 6 },
          { k: 'city', label: 'City', api: 'Addresses[].City', type: 'text', span: 3 },
          { k: 'region', label: 'Region', api: 'Addresses[].Region', type: 'text', span: 3 },
          { k: 'postcode', label: 'Postcode', api: 'Addresses[].PostalCode', type: 'text', span: 3 },
          { k: 'country', label: 'Country', api: 'Addresses[].Country', type: 'text', span: 3 },
        ],
      },
      {
        title: 'Financial details',
        fields: [
          { k: 'bankAccount', label: 'Bank account number', api: 'BankAccountDetails', type: 'text', span: 4 },
          { k: 'taxNumber', label: 'Tax ID number', api: 'TaxNumber', type: 'text', span: 4 },
          { k: 'defaultCurrency', label: 'Currency', api: 'DefaultCurrency', type: 'currency', span: 4 },
        ],
      },
      {
        title: 'Sales and purchase defaults',
        fields: [
          { k: 'salesAccount', label: 'Sales default account', api: 'Sales default account', type: 'account', span: 3 },
          { k: 'salesTax', label: 'Sales tax', api: 'AccountsReceivableTaxType', type: 'tax', span: 3 },
          { k: 'purchaseAccount', label: 'Purchases default account', api: 'Purchases default account', type: 'account', span: 3 },
          { k: 'purchaseTax', label: 'Purchase tax', api: 'AccountsPayableTaxType', type: 'tax', span: 3 },
        ],
      },
    ],
  },
  item: {
    title: 'New item',
    root: 'Sales',
    api: 'Item',
    endpoint: 'PUT /Items',
    lines: false,
    discount: false,
    backTo: 'list:items',
    sections: [
      {
        title: 'Item',
        fields: [
          { k: 'code', label: 'Code', api: 'Code', type: 'text', span: 3, req: true, ph: 'Unique item code' },
          { k: 'name', label: 'Name', api: 'Name', type: 'text', span: 6 },
          { k: 'tracked', label: 'Track inventory item', api: 'IsTrackedAsInventory', type: 'toggle', span: 3 },
          /*
            Both of these exist only for a tracked item, and both are
            restricted to one account type.

            "The inventory asset account for the item. The account must be
            of type INVENTORY. The COGSAccountCode in PurchaseDetails is
            also required to create a tracked item" — Xero's own OpenAPI
            description. Offering a bank account here, which this form did,
            was offering a refusal; and omitting cost of goods sold meant a
            tracked item could not be created at all.
          */
          {
            k: 'inventoryAccount',
            label: 'Inventory asset account',
            api: 'InventoryAssetAccountCode',
            type: 'account',
            span: 4,
            accounts: ['INVENTORY'],
            onlyWhen: 'tracked',
            req: true,
          },
        ],
      },
      {
        title: 'Purchase',
        fields: [
          { k: 'buyPrice', label: 'Cost price', api: 'PurchaseDetails.UnitPrice', type: 'num', span: 3 },
          { k: 'buyAccount', label: 'Purchase account', api: 'PurchaseDetails.AccountCode', type: 'account', span: 4 },
          /*
            Required alongside the inventory asset account, and only for a
            tracked item. Direct costs, which is the type Xero's own
            Products and services screen restricts this to.
          */
          {
            k: 'cogsAccount',
            label: 'Cost of goods sold account',
            api: 'PurchaseDetails.COGSAccountCode',
            type: 'account',
            span: 4,
            accounts: ['DIRECTCOSTS'],
            onlyWhen: 'tracked',
            req: true,
          },
          { k: 'buyTax', label: 'Tax rate', api: 'PurchaseDetails.TaxType', type: 'tax', span: 4 },
          { k: 'buyDesc', label: 'Description', api: 'PurchaseDescription', type: 'area', span: 12, ph: 'Text used on bills and purchase orders' },
        ],
      },
      {
        title: 'Sell',
        fields: [
          { k: 'sellPrice', label: 'Sale price', api: 'SalesDetails.UnitPrice', type: 'num', span: 3 },
          { k: 'sellAccount', label: 'Sales account', api: 'SalesDetails.AccountCode', type: 'account', span: 4 },
          { k: 'sellTax', label: 'Tax rate', api: 'SalesDetails.TaxType', type: 'tax', span: 4 },
          { k: 'sellDesc', label: 'Description', api: 'Description', type: 'area', span: 12, ph: 'Text used on invoices and quotes' },
        ],
      },
    ],
  },
};

export type IconKind = 'grid' | 'doc' | 'user' | 'box' | 'plug' | 'chart' | 'card';

export interface NavItem {
  label: string;
  screen: string;
  icon: IconKind;
  /**
   * What a person must be able to do for this to appear.
   *
   * Absent means everyone entitled to the company. This decides what is shown;
   * the API decides what is allowed, from the same table — a hidden button is
   * decoration, not a permission.
   */
  needs?: Capability;
  /**
   * A section only some clients were given.
   *
   * Different from `needs`, which asks what this person may do inside a
   * company everyone has. This asks whether the company has the thing at
   * all — a decision CFOAI makes per client, not a role. The API refuses
   * it independently; leaving it out of the menu only avoids offering a
   * link that would answer "not found".
   */
  feature?: 'training';
  count?: string;
  /** Hidden from the data-entry role. */
  admin?: boolean;
}

/** Which optional sections the company now open was given. */
export interface EnabledFeatures {
  training: boolean;
}

export interface NavGroup {
  key: string;
  items: NavItem[];
}

export const GROUPS: NavGroup[] = [
  {
    key: 'MAIN',
    items: [
      { label: 'Sales and purchases', screen: 'summary', icon: 'chart' },
      { label: 'Dashboard', screen: 'dash', icon: 'grid' },
      // The badge is counted from the queue itself, in the sidebar. The others
      // here carried numbers that were simply typed in, and are gone: a count
      // nobody computed is worse than no count at all.
      { label: 'Push queue', screen: 'queue', icon: 'box', needs: 'submit' },
      { label: 'Push log', screen: 'log', icon: 'doc', needs: 'operate' },
      /*
        Moved out of SALES when it stopped being only about sales.

        It was "Record a payment", and a payment against an invoice is all it
        did. It now also spends, receives and transfers — a bank charge is a
        purchase and a transfer is neither — so leaving it under SALES would
        file half of what it does in the wrong place.
      */
      { label: 'Money in and out', screen: 'payment', icon: 'card', needs: 'submit' },
    ],
  },
  {
    key: 'PURCHASES',
    items: [
      { label: 'Bills', screen: 'list:bills', icon: 'doc' },
      { label: 'Purchase orders', screen: 'list:pos', icon: 'doc' },
      { label: 'Suppliers', screen: 'list:suppliers', icon: 'user' },
    ],
  },
  {
    key: 'SALES',
    items: [
      { label: 'Invoices', screen: 'list:invoices', icon: 'doc' },
      { label: 'Quotes', screen: 'list:quotes', icon: 'doc' },
      { label: 'Customers', screen: 'list:customers', icon: 'user' },
      { label: 'Products and services', screen: 'list:items', icon: 'box' },
    ],
  },
  {
    /*
      The training centre's own operations. Nothing here is accounting: it is
      what a course is, when it runs and who is on it, which a ledger has
      nowhere to put. See api/src/functions/training.ts.
    */
    key: 'TRAINING',
    items: [
      { label: 'Training deliveries', screen: 'training:deliveries', icon: 'box', needs: 'submit', feature: 'training' },
      { label: 'Courses', screen: 'training:courses', icon: 'doc', needs: 'submit', feature: 'training' },
      { label: 'Training setup', screen: 'training:setup', icon: 'plug', needs: 'operate', feature: 'training' },
    ],
  },
  {
    /*
      Its own group, because a report is not a thing you file — it is a thing
      you read, and burying the profit and loss under SALES would hide the
      half of it that is costs.

      Only the profit and loss is Xero's own report. The other three are
      worked out from the documents, because Xero's API has no sales tax
      report and its aged reports take a required contactId. See
      api/src/functions/reports.ts.
    */
    key: 'REPORTS',
    items: [
      /*
        The practice's list, taken from what the client already stars in
        Xero. The two detail reports are deliberately absent: Xero serves
        them one contact at a time, so they are reached by clicking a name
        on the matching summary rather than from here.
      */
      { label: 'Profit and loss', screen: 'report:pl', icon: 'chart' },
      { label: 'Balance sheet', screen: 'report:balance', icon: 'doc' },
      { label: 'Aged receivables summary', screen: 'report:receivables', icon: 'doc' },
      { label: 'Aged payables summary', screen: 'report:payables', icon: 'doc' },
    ],
  },
  {
    key: 'ADMIN',
    items: [
      { label: 'People', screen: 'people', icon: 'user', needs: 'operate' },
      { label: 'Branding', screen: 'branding', icon: 'box', needs: 'operate' },
      { label: 'Payment methods', screen: 'methods', icon: 'card', needs: 'operate' },
      { label: 'Client connections', screen: 'conns', icon: 'plug', needs: 'administer' },
      { label: 'Xero connection', screen: 'xero', icon: 'plug', needs: 'administer' },
    ],
  },
];

/** Starting field values per form, matching the prototype's seeds. */
export function seedFields(kind: DocumentKind): Record<string, string | boolean> {
  // Dates are today and thirty days out, not the fixed dates the prototype
  // used. A document dated months ago is a quiet way to file something into
  // the wrong period.
  const today = new Date();
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  const plusDays = (n: number) => {
    const d = new Date(today);
    d.setDate(d.getDate() + n);
    return iso(d);
  };

  const base: Record<string, string | boolean> = {
    currencyCode: 'QAR',
    defaultCurrency: 'QAR',
    amountsAre: 'Exclusive',
    date: iso(today),
    country: 'Qatar',
  };

  switch (kind) {
    case 'invoice':
      return { ...base, contactId: '', dueDate: plusDays(30), reference: '' };
    case 'bill':
      return { ...base, contactId: '', dueDate: plusDays(15), reference: '' };
    case 'quote':
      return {
        ...base,
        contactId: '',
        expiryDate: plusDays(21),
        titleText: '',
        summary: '',
        terms: 'Prices valid for 21 days. 50% deposit on acceptance.',
      };
    case 'po':
      return {
        ...base,
        contactId: '',
        deliveryDate: '2026-09-08',
        deliveryAddress: 'Hero Factory, Industrial Area St 38, Doha',
        attentionTo: '',
        telephone: '+974 4144 7210',
        deliveryInstructions: '',
      };
    case 'item':
      return {
        ...base,
        tracked: true,
        inventoryAccount: '630',
        cogsAccount: '310',
        buyAccount: '310',
        sellAccount: '200',
        buyTax: 'INPUT',
        sellTax: 'OUTPUT',
        buyPrice: '',
        sellPrice: '',
      };
    case 'contact':
      return {
        ...base,
        salesAccount: '200',
        purchaseAccount: '420',
        salesTax: 'OUTPUT',
        purchaseTax: 'INPUT',
      };
  }
}

/**
 * A new document starts with one blank line.
 *
 * It used to start with invented ones — "Staff meals, week commencing 1 Sept"
 * against item code SRV-CTR — carried over from the design prototype, where
 * they existed to make the screenshots look real.
 *
 * Against a real organisation they are not merely untidy, they are fatal:
 * Xero rejects the whole invoice with "Item code 'SRV-CTR' is not valid",
 * because no such item exists in anyone's actual books. The very first invoice
 * anyone tried to send failed for exactly this reason.
 *
 * The tax type is the one thing worth defaulting, because it differs by
 * direction and getting it wrong is a real mistake rather than a blank field.
 */
export function seedLines(kind: DocumentKind) {
  if (!FORMS[kind].lines) return [];

  const purchase = kind === 'bill' || kind === 'po';
  return [
    {
      itemCode: '',
      description: '',
      qty: '',
      price: '',
      disc: '',
      accountCode: '',
      taxType: purchase ? 'INPUT' : 'OUTPUT',
    },
  ];
}
